import { useCallback, useState } from 'react';
import type { ApolloError } from '@apollo/client';
import { useAutoApplyDraftsLazyQuery } from './__generated__/ReviewPage.generated';
import { useDeleteAutoApplyApplicationDraftMutation } from '../../auto-apply/hooks/__generated__/DeleteAutoApplyApplicationDraft.generated';

const REVIEW_BEFORE_SUBMIT_DRAFT_BATCH_SIZE = 16;
const MAX_REVIEW_BEFORE_SUBMIT_DRAFT_CLEAR_ATTEMPTS = 5;

interface ClearReviewBeforeSubmitDraftsState {
    /** True for the entire cleanup operation (see note in the hook body). */
    loading: boolean;
    /** The last error from the underlying fetch/delete operations, if any. */
    error: ApolloError | undefined;
    /** True once either underlying operation has been invoked at least once. */
    called: boolean;
}

type ClearReviewBeforeSubmitDraftsFn = () => Promise<void>;

/**
 * Clears the jobseeker's remaining Review-Before-Submit drafts in batches (PSUB-4840).
 *
 * When a progressive-automation jobseeker updates their preference, any drafts prepared
 * under the old preference are stale and must be removed. The drafts are paged, so this
 * repeatedly fetches a batch and deletes it until a short/empty batch signals the end,
 * bailing out after a bounded number of attempts.
 *
 * Composes the generated `useAutoApplyDraftsLazyQuery` and
 * `useDeleteAutoApplyApplicationDraftMutation` hooks and derives `error`/`called`
 * straight from their result objects, returning an Apollo-mutation-like tuple:
 *
 *     const [clearReviewBeforeSubmitDrafts, { loading, error }] =
 *         useClearReviewBeforeSubmitDrafts();
 *
 * The action rejects on failure so callers can `await`/try-catch it.
 */
export const useClearReviewBeforeSubmitDrafts = (): [
    ClearReviewBeforeSubmitDraftsFn,
    ClearReviewBeforeSubmitDraftsState,
] => {
    // The only piece that can't be derived from the composed hooks. Each hook's
    // `loading` reflects a single operation and flips on/off between every fetch and
    // delete step of the loop; on its own it would flicker the UI and briefly
    // re-enable the caller's button between batches. This flag stays true for the
    // whole multi-step operation and is OR'd with the per-operation loading below.
    const [clearing, setClearing] = useState(false);

    // network-only so each page reflects the deletions already made in the previous
    // iteration rather than a cached first page.
    const [
        fetchDrafts,
        { loading: fetchDraftsLoading, error: fetchDraftsError, called: fetchDraftsCalled },
    ] = useAutoApplyDraftsLazyQuery({ fetchPolicy: 'network-only' });
    const [
        deleteDraft,
        { loading: deleteDraftLoading, error: deleteDraftError, called: deleteDraftCalled },
    ] = useDeleteAutoApplyApplicationDraftMutation();

    const clearReviewBeforeSubmitDrafts = useCallback<ClearReviewBeforeSubmitDraftsFn>(async () => {
        setClearing(true);
        try {
            for (
                let attempt = 0;
                attempt < MAX_REVIEW_BEFORE_SUBMIT_DRAFT_CLEAR_ATTEMPTS;
                attempt++
            ) {
                const result = await fetchDrafts({
                    variables: { first: REVIEW_BEFORE_SUBMIT_DRAFT_BATCH_SIZE },
                });
                // The lazy-query execute resolves with an `error` field on failure
                // rather than rejecting, so surface it explicitly.
                if (result.error) {
                    throw result.error;
                }
                const draftIds =
                    result.data?.findReviewBeforeSubmitDrafts?.drafts.map(
                        (draft) => draft.applicationDraftId,
                    ) ?? [];

                if (draftIds.length === 0) {
                    return;
                }

                const deleteResults = await Promise.all(
                    draftIds.map((applicationDraftId) =>
                        deleteDraft({ variables: { input: { applicationDraftId } } }),
                    ),
                );

                deleteResults.forEach((deleteResult, index) => {
                    const applicationDraftId = draftIds[index];
                    if (
                        deleteResult.data?.draftManagementApplicationDraft.delete
                            ?.applicationDraftId !== applicationDraftId
                    ) {
                        throw new Error(
                            `Failed to delete Review Before Submit draft ${applicationDraftId}`,
                        );
                    }
                });

                // A partial batch means there is no further page to fetch.
                if (draftIds.length < REVIEW_BEFORE_SUBMIT_DRAFT_BATCH_SIZE) {
                    return;
                }
            }

            throw new Error(
                `Failed to clear Review Before Submit drafts after ${MAX_REVIEW_BEFORE_SUBMIT_DRAFT_CLEAR_ATTEMPTS} attempts of ${REVIEW_BEFORE_SUBMIT_DRAFT_BATCH_SIZE}`,
            );
        } finally {
            setClearing(false);
        }
    }, [fetchDrafts, deleteDraft]);

    return [
        clearReviewBeforeSubmitDrafts,
        {
            loading: clearing || fetchDraftsLoading || deleteDraftLoading,
            error: fetchDraftsError ?? deleteDraftError,
            called: fetchDraftsCalled || deleteDraftCalled,
        },
    ];
};
